import React, { useEffect, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { setPreferredOrgId } from '../../../utils/preferredOrg';
import { fetchOrgs, switchOrg } from './api';
import type { AdminOrg, OrgsMeta } from './types';
import { primaryBtnClass, secondaryBtnClass } from '../styles';

interface ChooseOrganizationProps {
  meta: OrgsMeta;
}

const ChooseOrganization: React.FC<ChooseOrganizationProps> = ({ meta }) => {
  const [q, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<AdminOrg[]>([]);
  const [count, setCount] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [switching, setSwitching] = useState<number | null>(null);

  const load = async (nextPage = page) => {
    setLoading(true);
    setError(null);
    try {
      const payload = await fetchOrgs({
        q: q.trim(),
        page: nextPage,
      });
      setData(payload.results);
      setCount(payload.count);
      setPage(payload.page);
      setPageSize(payload.page_size);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUseOrg = async (org: AdminOrg) => {
    if (!org.is_active) return;
    setSwitching(org.id);
    setError(null);
    try {
      await switchOrg(org.id);
      setPreferredOrgId(meta.current_user_id, org.id);
      window.location.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to switch organization');
      setSwitching(null);
    }
  };

  const ordered = [...data].sort((a, b) => {
    const aIsCurrent = a.id === meta.current_org_id;
    const bIsCurrent = b.id === meta.current_org_id;
    if (aIsCurrent && !bIsCurrent) return -1;
    if (!aIsCurrent && bIsCurrent) return 1;
    return a.name.localeCompare(b.name);
  });

  const totalPages = Math.max(1, Math.ceil(count / pageSize));

  return (
    <div>
      <p className="text-sm text-[#626262] mb-4">
        Choose an organization to view the platform as that organization. This is the same switch as
        the Organization menu, with more detail on each card.
      </p>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative w-64 max-w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A9A]" />
          <input
            value={q}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void load(1);
            }}
            placeholder="Search organizations"
            className="h-10 w-full rounded-md border border-[#D1D1D1] pl-9 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#3da3d4]"
          />
        </div>
        <button type="button" onClick={() => void load(1)} className={secondaryBtnClass}>
          Search
        </button>
      </div>
      {error && (
        <div className="mb-3 rounded-md bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>
      )}
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[#8A8A8A] py-6">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading organizations…
        </div>
      ) : data.length === 0 ? (
        <div className="rounded-lg border border-[#E8E8E8] px-3 py-8 text-sm text-[#8A8A8A]">
          No organizations match this search.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {ordered.map((org) => {
            const current = org.id === meta.current_org_id;
            return (
              <div
                key={org.id}
                className={`rounded-xl border p-4 flex flex-col gap-3 ${
                  current ? 'border-[#3da3d4] bg-[#F3F8FC]' : 'border-[#E8E8E8] bg-white'
                } ${org.is_active ? '' : 'opacity-70'}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-base font-semibold text-[#0C0B0B] leading-snug">{org.name}</h3>
                  {current && (
                    <span className="shrink-0 text-[11px] font-medium text-[#3da3d4]">Current</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {org.tier_name && (
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#F3F3F3] text-[#4A4A4A]">
                      {org.tier_name}
                    </span>
                  )}
                  {org.currency_code && (
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#F3F3F3] text-[#4A4A4A]">
                      {org.currency_code}
                    </span>
                  )}
                  <span
                    className={`text-[11px] px-2 py-0.5 rounded-full ${
                      org.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-[#F3F3F3] text-[#8A8A8A]'
                    }`}
                  >
                    {org.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <dl className="text-[12px] text-[#626262] space-y-1">
                  <div>
                    <span className="text-[#8A8A8A]">Account manager </span>
                    {org.account_manager_name || '—'}
                  </div>
                  <div>
                    <span className="text-[#8A8A8A]">Org admin </span>
                    {org.org_admin_name || '—'}
                  </div>
                  <div>
                    {org.client_member_count} client member
                    {org.client_member_count === 1 ? '' : 's'}
                  </div>
                </dl>
                <button
                  type="button"
                  disabled={!org.is_active || current || switching === org.id}
                  onClick={() => void handleUseOrg(org)}
                  className={`${primaryBtnClass} mt-auto self-start`}
                >
                  {switching === org.id && (
                    <Loader2 className="w-4 h-4 animate-spin inline mr-1.5" />
                  )}
                  {current ? 'In use' : 'Use this organization'}
                </button>
              </div>
            );
          })}
        </div>
      )}
      <div className="mt-3 flex items-center justify-between text-[12px] text-[#626262]">
        <span>
          {count} organization{count === 1 ? '' : 's'}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="text-[#3da3d4] disabled:opacity-30"
            disabled={page <= 1 || loading}
            onClick={() => void load(page - 1)}
          >
            Previous
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            className="text-[#3da3d4] disabled:opacity-30"
            disabled={page >= totalPages || loading}
            onClick={() => void load(page + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChooseOrganization;
